import React from 'react';

import ImageBase from './ImageBase';
import { Icon } from '../Icon';

type TLogo = {
  src: string;
  alt: string;
  size?: 's' | 'm' | 'l';
  variant?: 'fill' | 'fit';
  className?: string;
  placeholderIconName: string;
};

function shouldRenderPlaceholder(src: string, placeholderIconName: string) {
  return !Boolean(src) && Boolean(placeholderIconName);
}

function getWidthHeight(size: TLogo['size'] = 'm') {
  const sizes = {
    s: '32px',
    m: '48px',
    l: '64px',
  };

  return { width: sizes[size], height: sizes[size] };
}

function createLogoClassName(
  shouldShowPlaceholderIcon: boolean,
  className: string | undefined,
  variant: TLogo['variant'] = 'fill',
) {
  const placeholderClass = shouldShowPlaceholderIcon ? 'hz-Image--logo-placeholder' : '';
  const variantClass = `hz-Image--logo-${variant}`;
  return [placeholderClass, variantClass, className].join(' ').trim();
}

type TGetLogoProps = {
  alt: string;
  size: TLogo['size'];
  variant: TLogo['variant'];
  className?: string;
  shouldShowPlaceholderIcon: boolean;
  rest: Record<string, unknown>;
};

function getLogoProps({ alt, size, variant, className, shouldShowPlaceholderIcon, rest }: TGetLogoProps) {
  const as: keyof React.JSX.IntrinsicElements = shouldShowPlaceholderIcon ? 'div' : 'img';

  return {
    ...rest,
    as,
    kind: 'logo' as keyof typeof ImageBase,
    alt,
    className: createLogoClassName(shouldShowPlaceholderIcon, className, variant),
    style: { ...getWidthHeight(size) },
  };
}

const Logo = ({ src, alt, size = 'm', variant = 'fill', className, placeholderIconName, ...rest }: TLogo) => {
  const shouldShowPlaceholderIcon = shouldRenderPlaceholder(src, placeholderIconName);
  const _props = getLogoProps({
    shouldShowPlaceholderIcon,
    rest,
    alt,
    className,
    size,
    variant,
  });

  if (shouldShowPlaceholderIcon) {
    return (
      <ImageBase {..._props}>
        <Icon name={placeholderIconName} color="background-default" className="hz-Image--placeholder-icon" />
      </ImageBase>
    );
  }

  return (
    <span>
      <ImageBase {..._props} src={src} />
    </span>
  );
};

export default Logo;
